import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { createGroup } from "../../../api/access/accessApi";
import type { AccessList, SystemGroup } from "../../../api/access/accessModel";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import type { GroupNamer } from "../model/groupNames";
import { NEEDS_REASON } from "../model/permissionCatalogue";

/** The groups a project's new group may start like (redesign R3-2: from a starter group's set). */
const STARTERS: ReadonlySet<SystemGroup> = new Set(["readers", "contributors", "curators"]);

interface NewGroupDialogProps {
  list: AccessList;
  namer: GroupNamer;
  onCreated: (groupId: string) => void;
  onClose: () => void;
}

/**
 * Makes a group at the bottom of the scope's list. A project's group may start like one of
 * its starter groups; an installation group may sit inside another.
 */
export default function NewGroupDialog({ list, namer, onCreated, onClose }: NewGroupDialogProps) {
  const { t } = useTranslation();
  const formId = useId();
  const [name, setName] = useState("");
  const [like, setLike] = useState("");
  const [parentId, setParentId] = useState("");
  const { state, submit } = useSubmission();
  const pending = state.status === "submitting";
  const starters = list.groups.filter((group) => group.system !== null && STARTERS.has(group.system));
  const parents = list.projectId === null ? list.groups.filter((group) => group.system === null) : [];
  return (
    <ModalShell
      title={t("access.newGroup")}
      onClose={onClose}
      closeDisabled={pending}
      size="md"
      zIndexClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" disabled={pending} onClick={onClose}>{t("common.cancel")}</AppButton>
          <AppButton type="submit" form={formId} variant="primary" size="sm" disabled={pending || name.trim() === ""}>{t("access.create")}</AppButton>
        </div>
      }
    >
      <form id={formId} className="space-y-3 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          // The server's keys are switched on afterwards, with a reason (GS-4); a new group never starts with them.
          const grants = (list.groups.find((group) => group.groupId === like)?.grants ?? []).filter((grant) => grant.permission !== NEEDS_REASON);
          submit(async () => {
            const groupId = await createGroup(list.projectId, name.trim(), grants, parentId === "" ? null : parentId);
            onClose();
            onCreated(groupId);
          });
        }}>
        <Field label={t("access.name")}>
          {(control) => <input {...control} className={FORM_CONTROL_CLASS} value={name} disabled={pending} onChange={(event) => { setName(event.target.value); }} />}
        </Field>
        {starters.length === 0 ? null : (
          <Field label={t("access.startsLike")}>
            {(control) => (
              <select {...control} className={FORM_CONTROL_CLASS} value={like} disabled={pending} onChange={(event) => { setLike(event.target.value); }}>
                <option value="">{t("access.startsEmpty")}</option>
                {starters.map((group) => <option key={group.groupId} value={group.groupId}>{namer.name(group.groupId)}</option>)}
              </select>
            )}
          </Field>
        )}
        {parents.length === 0 ? null : (
          <Field label={t("access.inside")}>
            {(control) => (
              <select {...control} className={FORM_CONTROL_CLASS} value={parentId} disabled={pending} onChange={(event) => { setParentId(event.target.value); }}>
                <option value="">{t("access.atTop")}</option>
                {parents.map((group) => <option key={group.groupId} value={group.groupId}>{namer.name(group.groupId)}</option>)}
              </select>
            )}
          </Field>
        )}
        {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      </form>
    </ModalShell>
  );
}
